import type { ApiClient } from "./api";
import { friendlyMessage } from "./api";
import type { SearchResult } from "./types";

const DEBOUNCE_MS = 600;

export interface SearchUI {
  input: HTMLInputElement;
  resultsList: HTMLUListElement;
  onPick: (r: SearchResult) => void;
  onError?: (msg: string) => void;
}

export function wireSearch(api: ApiClient, ui: SearchUI): () => void {
  let debounceTimer: number | undefined;
  let abortController: AbortController | null = null;

  const render = (results: SearchResult[]): void => {
    ui.resultsList.innerHTML = "";
    if (results.length === 0) {
      const li = document.createElement("li");
      li.textContent = "no places found - try a different query";
      ui.resultsList.appendChild(li);
      ui.resultsList.hidden = false;
      return;
    }
    for (const r of results) {
      const li = document.createElement("li");
      li.textContent = r.display_name;
      li.tabIndex = 0;
      li.addEventListener("click", () => {
        ui.resultsList.hidden = true;
        ui.resultsList.innerHTML = "";
        ui.input.value = r.display_name;
        ui.onPick(r);
      });
      ui.resultsList.appendChild(li);
    }
    ui.resultsList.hidden = false;
  };

  const runSearch = async (q: string): Promise<void> => {
    if (q.trim().length < 3) {
      ui.resultsList.hidden = true;
      return;
    }
    if (abortController) abortController.abort();
    abortController = new AbortController();
    try {
      const results = await api.search(q, abortController.signal);
      render(results);
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") return;
      if (ui.onError) ui.onError(friendlyMessage(err));
    }
  };

  const onInput = (): void => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      runSearch(ui.input.value);
    }, DEBOUNCE_MS);
  };

  const onSubmit = (e: Event): void => {
    e.preventDefault();
    window.clearTimeout(debounceTimer);
    runSearch(ui.input.value);
  };

  ui.input.addEventListener("input", onInput);
  const form = ui.input.closest("form");
  form?.addEventListener("submit", onSubmit);

  return (): void => {
    ui.input.removeEventListener("input", onInput);
    form?.removeEventListener("submit", onSubmit);
    if (abortController) abortController.abort();
    window.clearTimeout(debounceTimer);
  };
}
